import * as React from 'react';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { LeavePageConfirm } from '../LeavePageConfirm/LeavePageConfirm';
import {
  Button,
  Col,
  Container,
  FormGroup,
  FormText,
  Label,
  ListGroup,
  ListGroupItem,
  Navbar,
  NavbarBrand,
  Row,
} from 'reactstrap';
import { useParams } from 'react-router';
import { authAxios } from '../../services/AxiosService';
import { useHistory } from 'react-router-dom';
import { notifyError, notifySuccess } from '../../services/ToastService';
import { formatDateTime } from '../../formatters/DateTimeFormatter';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { FileContext } from './FileContext';
import { getUserId } from '../../services/AuthenticationService';
import { FormattedText } from '../FormattedText';
import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import { FileUpload, OnFileDrop } from '../FileUpload/FileUpload';
import { IDocument } from './Documents/Documents';
import { DocumentListItem } from './Documents/DocumentListItem';
import DatePicker from 'react-datepicker';
import LoadingService from '../Loading/LoadingService';
import * as FileUploadService from '../FileUpload/FileUploadService';
import { FaPrint } from 'react-icons/fa';
import { UsersContext } from '../../contexts/UsersContext';
import { INCIDENT_DOCUMENT_TYPE, sanitizeHTMLConfig } from '../../contexts/DomainContext';
import { MyUserContext } from '../../contexts/MyUserContext';
import { IIncident } from '../../components/Files/FileIncidents';
const DOMPurify = require('dompurify');

interface IFileIncidentsDetails {
  onSave: () => void;
  onCancel: () => void;
}

export const FileIncidentsDetails: React.FC<IFileIncidentsDetails> = (props) => {
  const fileContext = useContext(FileContext);
  const { isSuperAdmin } = useContext(MyUserContext);
  const [isDirty, setIsDirty] = useState(false);
  const [textBoxEditor, setTextBoxEditor] = useState<EditorState>(EditorState.createEmpty());
  const [lastUpdated, setLastUpdated] = useState<number>();
  const [date, setDate] = useState<Date>(new Date());
  const [docsToUpload, setDocsToUpload] = useState<File[]>([]);
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<IDocument[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const params = useParams<{ fileId: string; incidentId?: string }>();
  const history = useHistory();
  const [printOption, setPrintOption] = useState('');
  const usersContext = useContext(UsersContext);
  const users = usersContext.users;
  const [incident, setIncident] = useState<IIncident | undefined>();
  const isNewIncidentOrCanEdit = !params.incidentId || incident?.userCanEdit;

  useEffect(() => {
    if (params.incidentId) {
      authAxios.get('/api/files/admin/' + params.fileId + '/incidents/' + params.incidentId).then((response) => {
        const incident: IIncident = response.data;
        setIncident(incident);
        setTextBoxEditor(
          EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft(incident.text).contentBlocks))
        );
        setLastUpdated(incident.dateLastUpdated);
      });
      authAxios
        .get('/api/files/admin/' + params.fileId + '/incidents/' + params.incidentId + '/documents')
        .then((response) => {
          setDocuments(response.data);
        });
    }
  }, [params.incidentId, params.fileId]);

  useEffect(() => {
    if (printOption !== '') {
      window.print();
      setPrintOption('');
    }
  }, [printOption]);

  const doNewDocumentToUpload = (incidentId?: string) => {
    const files = docsToUpload;
    const newDocumentPath = '/api/documents/add-incident-document/' + params.fileId + '/' + incidentId;

    if (!incidentId) {
      console.log('Nowhere to upload these... bailing!', files);
      return;
    }
    console.log("Let's upload these ", files);

    const loadingKey: string = LoadingService.add();

    const doUploadOneFile = (file: File, index: number) => {
      return FileUploadService.uploadFile(file, newDocumentPath).then((response: any) => {
        if (response.data.status === 'OK') {
          setDocuments((docs: IDocument[]) => [...docs, response.data.document as IDocument]);
          setDocsToUpload((d) => (d.length <= 1 ? [] : [...d.slice(0, index), ...d.slice(index + 1)]));
        }
      });
    };

    const sequenceUploads = Array.from(files).reduce((p, file: File, i) => {
      return p.then(() => doUploadOneFile(file, i).catch((err) => console.log('Upload failed.', err)));
    }, Promise.resolve());

    sequenceUploads
      .then(() => {
        console.log('Uploaded all files.');
        fileContext.setDocumentUploaded(!fileContext.documentUploaded);
        LoadingService.remove(loadingKey);
      })
      .catch((error) => {
        console.log('One or more upload failed.', error);
        LoadingService.remove(loadingKey);
      });
  };

  const save = () => {
    if (params.incidentId) {
      //update
      authAxios
        .post('/api/files/admin/update-incident', {
          fileId: params.fileId,
          incidentId: params.incidentId,
          text: FormattedText.getContent(textBoxEditor),
        })
        .then((response) => {
          if (response.data.status === 'OK') {
            setIsDirty(false);
            if (documentsToDelete && documentsToDelete.length > 0) {
              documentsToDelete.forEach((d) => {
                authAxios.post('/api/documents/remove-incident-document', {
                  fileId: params.fileId,
                  incidentId: params.incidentId,
                  documentId: d.documentId,
                });
              });
            }
            notifySuccess('Incident updated.');
            setLastUpdated(new Date().getTime());
            doNewDocumentToUpload(params.incidentId);
          } else {
            const errorMessage = response.data?.errorMessage;
            notifyError(errorMessage ? errorMessage : 'Error updating incident.');
          }
        });
    } else {
      //create
      authAxios
        .post('/api/files/admin/create-incident', {
          fileId: params.fileId,
          text: FormattedText.getContent(textBoxEditor),
          date: date.getTime(),
        })
        .then((response) => {
          if (response.data.status === 'OK') {
            setIsDirty(false);
            notifySuccess('Incident created.');
            doNewDocumentToUpload(response.data.incidentId);
            fileContext.setIncidents((n) =>
              n.concat([
                {
                  incidentId: response.data.incidentId,
                  text: FormattedText.getContent(textBoxEditor),
                  date: date.getTime(),
                  userCreated: getUserId(),
                  dateLastUpdated: new Date().getTime(),
                  userUpdated: getUserId(),
                  documents: docsToUpload.map((d) => {
                    return {
                      documentId: Math.random().toString(),
                      documentTypeId: INCIDENT_DOCUMENT_TYPE,
                      fileName: d.name,
                      documentState: '',
                      uploadId: '',
                    };
                  }),
                  fileId: params.fileId,
                  userCanEdit: true,
                },
              ])
            );
          } else {
            notifyError('Error creating incident.');
          }
        });
      props.onSave();
    }
  };

  const deleteIncident = () => {
    if (params.incidentId) {
      authAxios
        .post('/api/files/admin/delete-incident', { fileId: params.fileId, incidentId: params.incidentId })
        .then((response) => {
          if (response.data.status === 'OK') {
            setIsDirty(false);
            notifySuccess('Incident deleted.');
            history.push({
              pathname: '/files/' + params.fileId + '/incidents',
              state: { deletedIncidentId: params.incidentId },
            });
          } else {
            notifyError('Error deleting incident.');
          }
        });
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      props.onCancel();
    }
  };

  const handleFileDrop: OnFileDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setIsDirty(true);
      setDocsToUpload((files) => [...files, ...acceptedFiles]);
    }
  };

  const removeDocument = (document: IDocument) => {
    setIsDirty(true);
    setDocumentsToDelete((docs) => docs.concat(document));
    setDocuments((files) => {
      const filteredFiles = files.filter((file) => {
        if ('fileName' in file && 'fileName' in document) {
          return file.fileName !== document.fileName;
        }
        return false;
      });
      return filteredFiles;
    });
  };

  const removeDocumentToUpload = (document: File) => {
    setDocsToUpload((files) => {
      const filteredFiles = files.filter((file) => {
        if ('name' in file && 'name' in document) {
          return file.name !== document.name;
        }
        return false;
      });
      return filteredFiles;
    });
  };

  return (
    <>
      <div>
        <header className={'report-header hide-printable'}>
          <h2 className='text-center'>CYFN File {fileContext.file.fileNumber} Incidents</h2>
        </header>
        <ListGroup>
          <ListGroupItem key={incident?.incidentId} className={'hide-printable'}>
            <Row>
              <Col md={12} lg={2} className={'text-lg-right'}>
                <strong>
                  {users.find((u) => u.userId === incident?.userCreated)
                    ? users.find((u) => u.userId === incident?.userCreated)?.lastName.toUpperCase() +
                      ', ' +
                      users.find((u) => u.userId === incident?.userCreated)?.firstName
                    : ''}
                </strong>
                <div className={'text-muted small mb-2'}>
                  {incident?.date ? formatDateTime(new Date(incident?.date)) : ''}
                </div>
              </Col>
              <Col md={8} lg={7} xl={6} className={'mb-3 mb-md-0'}>
                <main
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(incident?.text ? incident.text : '', sanitizeHTMLConfig),
                  }}
                />
              </Col>
              <Col>
                {documents && documents.length > 0 && (
                  <div>
                    <h6>Documents</h6>
                    <ul>
                      {documents.map((d) => (
                        <li key={d.documentId}>
                          <Button
                            color={'link'}
                            className={'p-0'}
                            disabled={!d.uploadId}
                            style={{ wordBreak: 'break-all', textAlign: 'left' }}
                          >
                            {d.fileName}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Col>
            </Row>
          </ListGroupItem>
        </ListGroup>
      </div>
      <LeavePageConfirm isDirty={isDirty} />
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
        }}
        className={'no-print'}
      >
        <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom sticky-top'}>
          <NavbarBrand>{params.incidentId ? 'Edit Incident' : 'Add Incident'}</NavbarBrand>
          <div className={'ml-auto d-flex flex-row-reverse'}>
            {isNewIncidentOrCanEdit && (
              <Button color={'primary'} className={'ml-2'} onClick={save} disabled={!isDirty}>
                Save Incident
              </Button>
            )}
            {params.incidentId ? (
              <div>
                <Button
                  color='warning'
                  className={'ml-auto'}
                  onClick={() => setPrintOption(params.incidentId ? params.incidentId : '')}
                >
                  <FaPrint /> Print
                </Button>
                {isSuperAdmin && (
                  <Button color='danger' className={'ml-2'} onClick={() => setDeleteModalOpen(true)}>
                    Delete Incident
                  </Button>
                )}
              </div>
            ) : (
              <Button color='link' className={'ml-auto'} onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>
        </Navbar>
        <Container fluid={true}>
          <Row className={'mt-3'}>
            <Col md={12} lg={2} className={'text-lg-right'}>
              <Label htmlFor={'incident'} className={'text-body h6 mb-0 font-weight-bold'}>
                Incident
              </Label>
              <br />
              {params.incidentId && (
                <div className={'text-muted small mb-2'}>
                  &nbsp;&nbsp;Last Updated:
                  <br /> {lastUpdated !== undefined ? formatDateTime(new Date(lastUpdated)) : ''}
                </div>
              )}
            </Col>
            <Col md={8} lg={7} xl={6}>
              <FormGroup>
                <FormattedText
                  name={'incident'}
                  editorState={textBoxEditor}
                  onEditorStateChange={(editorState: EditorState) => {
                    setTextBoxEditor(editorState);
                    editorState.getUndoStack().size > 0 && setIsDirty(true);
                  }}
                  autoFocus={true}
                  placeholder={'Add your incident here…'}
                  readOnly={!isNewIncidentOrCanEdit}
                />
              </FormGroup>
              {!params.incidentId && (
                <FormGroup>
                  <Label htmlFor={'incidentDate'}>Date</Label>
                  <DatePicker
                    selected={date}
                    id={'incidentDate'}
                    onChange={(date) => {
                      if (date) {
                        setDate(date);
                      }
                    }}
                    className={'form-control'}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dateFormat='MMM dd, yyyy'
                    disabled={!isNewIncidentOrCanEdit}
                  />
                </FormGroup>
              )}
            </Col>
            <Col>
              {isNewIncidentOrCanEdit && (
                <FileUpload isUploading={false} onDrop={handleFileDrop} uploadSuccessful={false} />
              )}
              {(documents.length > 0 || docsToUpload.length > 0) && (
                <ListGroup className={'my-3'}>
                  {documents?.map((d, i) => (
                    <DocumentListItem
                      key={i}
                      name={d.fileName}
                      onDelete={() => removeDocument(d)}
                      onClick={() => alert('click')}
                      showDelete={isNewIncidentOrCanEdit}
                    />
                  ))}
                  {docsToUpload.map((d, i) => (
                    <DocumentListItem
                      key={i}
                      name={d.name}
                      onDelete={() => removeDocumentToUpload(d)}
                      onClick={() => alert('click')}
                      showDelete={isNewIncidentOrCanEdit}
                    />
                  ))}
                </ListGroup>
              )}
              {isNewIncidentOrCanEdit && (
                <FormText color='muted' className={'my-3'}>
                  New documents will be uploaded when this incident is saved.
                </FormText>
              )}
            </Col>
          </Row>
        </Container>
      </form>
      <ConfirmModal
        isOpen={deleteModalOpen}
        title={'Delete Incident'}
        onConfirm={() => {
          setDeleteModalOpen(false);
          deleteIncident();
        }}
        onCancel={() => setDeleteModalOpen(false)}
        confirmButton={'Delete Incident'}
        confirmButtonColor={'danger'}
      >
        <div>Are you sure you want to delete this incident?</div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={cancelModalOpen}
        title={`You have unsaved changes.`}
        onConfirm={() => {
          setCancelModalOpen(false);
          props.onCancel();
        }}
        onCancel={() => setDeleteModalOpen(false)}
        confirmButton={'Leave this Page'}
        cancelButton={'Stay on Page'}
      />
    </>
  );
};
